import { useMemo, useState } from 'react'

import { isEmpty } from 'lodash'
import { makeTraceDetails } from 'common'
import { useScreenFinishLoadTac } from 'common/hooks/useScreenFinishLoadTac'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { useNavigate } from 'react-router-dom'
import { TEntity, TUseEntityMenuReturn } from 'contexts/app-context/types'
import { appContextReducerActions, getEntityOrganization } from 'contexts/app-context/utils'
import { THeaderRightIcon } from 'components/header/types'
import { logBridgeError, sendSelectContent } from 'common/helpers'
import { IS_PRE_PROD, Routes } from 'common/constants'

import { USE_ENTITY_MENU_RETURN_DEFAULT_OBJ } from '../utils'

export const useEntityMenu = (): TUseEntityMenuReturn => {
  const [showBottomsheet, setShowBottomsheet] = useState(false)
  const navigate = useNavigate()

  const {
    entityMenuContent,
    userEntities,
    userAccountType,
    isAppLoading,
    isAppError,
    dispatch,
    userSelectedEntity,
    entitiesOrganizations,
  } = useAppContext()

  const onClick = async (entity: TEntity, route: string) => {
    const entityOrganization = getEntityOrganization({
      entitiesOrganizations,
      entity,
    })
    const isUSBranchEntity = entityOrganization === 'US_BRANCH'

    if (entityOrganization) dispatch(appContextReducerActions.setIsUSBranch(isUSBranchEntity))

    if (userSelectedEntity) {
      dispatch(appContextReducerActions.setUserSelectedEntity(undefined))
      dispatch(appContextReducerActions.setPrivacyCenterContent(undefined))
    }

    try {
      await sendSelectContent({
        flow: 'entity_menu',
        step: 'root',
        screen_name: Routes.ROOT,
        element_name: 'selectEntity',
        element_type: 'button',
        parent_name: 'EntityMenu',
        scenario: JSON.stringify({
          screen: Routes.ROOT,
          product: userSelectedEntity,
          entity,
          ref_type: 'button',
          content_action: 'touch',
        }),
      })
    } catch (error: unknown) {
      logBridgeError('send entity menu tic', error)
    }

    dispatch(appContextReducerActions.setUserSelectedEntity(entity))
    navigate(route)
  }

  const title = useMemo(() => {
    if (!entityMenuContent?.content?.headerSection?.title) return ''
    if (IS_PRE_PROD) {
      return `${entityMenuContent?.content?.headerSection.title} - BETA`
    }
    return entityMenuContent?.content?.headerSection.title
  }, [entityMenuContent])

  const content = entityMenuContent?.content

  const entities = userEntities ?? []

  const isActive = entityMenuContent?.isActive

  const hasContentError = !content || isEmpty(content)

  const isError = isAppError || hasContentError || !isActive

  const isLoadingPath = isAppLoading || userAccountType === 'PJ'

  useScreenFinishLoadTac({
    name: 'EntityMenu',
    screen: Routes.ROOT,
    action: 'EntityMenuFinishLoad',
    entity: userSelectedEntity,
    shouldSend: !isLoadingPath && !isError && !isEmpty(entities) && !!content,
    logLabel: 'send entity menu finish tac',
  })

  // if the user is a PJ, we don't need to load the entity menu as this page will be skipped and the home page will be loaded instead
  if (isAppLoading || userAccountType === 'PJ')
    return {
      ...USE_ENTITY_MENU_RETURN_DEFAULT_OBJ,
      isLoading: true,
    }

  if (isError) {
    const traceDetails = makeTraceDetails(!!isActive, {
      content: content ?? {},
      entities: entities ?? [],
      onClick,
    })

    return {
      ...USE_ENTITY_MENU_RETURN_DEFAULT_OBJ,
      isLoading: false,
      isError: true,
      traceDetails,
    }
  }

  const bottomsheetClickHandler = () => {
    setShowBottomsheet((current) => !current)
  }

  const rightIcon: THeaderRightIcon = {
    iconRef: content.headerSection.icon,
    color: 'var(--secondary500)',
    size: 24,
    onClick: bottomsheetClickHandler,
  }

  return {
    title,
    content,
    showBottomsheet,
    rightIcon,
    entities,
    traceDetails: '',
    isError: false,
    isLoading: false,
    bottomsheetClickHandler,
    onClick,
  }
}
